import * as Collections from 'typescript-collections'
import * as ko from 'knockout';
import channel from '@/cqs/channel';
import * as q from '@/cqs/remotequeries';
import config from './config';
import ctx from './ctx';
import { flashError } from './helpers';
import trelloStore from './trellostore';

const lastAccessKey = "LAST_ACCESS";
const cacheTimeoutInSeconds = 3;

var boardData: ko.Observable<BoardData> = ko.observable(null).extend({notify: "always"});
var cardBackData: ko.Observable<q.GetCardBackDataResult> = ko.observable(null).extend({notify: "always"});
var hasAccess: ko.Observable<boolean> = ko.observable(null).extend({notify: "always"});
var boardLastQueriedAt: Date = null;
var hasAccessLastQueriedAt: Date = null;
var isBoardQueryRunning = false;
var isHasAccessQueryRunning = false;
var isCardBackQueryRunning = false;


var getBoardData: (t: any) => Promise<BoardData> = (t: any) => {
    return new Promise<BoardData>(async (resolve, reject) => {
        var isAuthenticated = await config(t).isUserAuthenticated();

        if(!isAuthenticated) {
            resolve(null);
        }
        else {
            if(isBoardQueryRunning) {
                var sub = boardData.subscribe(newValue => {
                    resolve(newValue);
                    sub.dispose();
                });
            }
            else {
                var hasCachedData = boardLastQueriedAt != null && Date.now() - boardLastQueriedAt.getTime() < cacheTimeoutInSeconds * 1000;

                if(hasCachedData) {
                    resolve(boardData());
                }
                else {
                    isBoardQueryRunning = true;
                    try {
                        var boardDataResult : q.GetBoardDataResult = await channel(t).executeQuery(new q.GetBoardData());
                        var cards = new Collections.Dictionary<string, q.CardData>();
                        boardDataResult.Cards.forEach(x => cards.setValue(x.Id, removeDefaultContextFromCard(x)));
                        boardData(new BoardData(cards, boardDataResult.StorymapUrl, boardDataResult.IsEditor, boardDataResult.CanSetEstimation, boardDataResult.ColorsAndAnnotations));
                        boardLastQueriedAt = new Date();
                    }
                    finally {
                        isBoardQueryRunning = false;
                    }
                    resolve(boardData());
                }
            }
        }
    });
}

export var notifyAccessChanged = async (t) => {
    await trelloStore(t).setBoardShared(lastAccessKey, new Date());
}

export var checkAccess: (t: any) => Promise<boolean> = (t: any) => {
    return new Promise<boolean>(async (resolve, reject) => {
        await trelloStore(t).getBoardShared(lastAccessKey); // Hogy újra kiértékelődjön módosításkor
        if(hasAccess() != null && hasAccessLastQueriedAt != null && Date.now() - hasAccessLastQueriedAt.getTime() < cacheTimeoutInSeconds * 1000) {
            resolve(hasAccess());
        }
        else {
            var context = await ctx(t).get();
            if(!context.isReady) {
                resolve(false);
            }
            else {
                var isAuthenticated = await config(t).isUserAuthenticated();
                if(!isAuthenticated) {
                    resolve(false);
                }
                else {
                    if(isHasAccessQueryRunning) {
                        var sub = hasAccess.subscribe(newValue => {
                            resolve(newValue);
                            sub.dispose();
                        });
                    }
                    else {
                        isHasAccessQueryRunning = true;
                        try {
                            var hasAccessResult: boolean = await channel(t).executeQuery(new q.CheckAccess());
                            hasAccess(hasAccessResult);
                        }
                        catch(e) {
                            hasAccess(false);
                        }
                        hasAccessLastQueriedAt = new Date();
                        await trelloStore(t).setBoardShared(lastAccessKey, new Date());
                        isHasAccessQueryRunning = false;
                        resolve(hasAccess());
                    }
                }
            }
        }
    });
}


export var getDataForCardFront: (t: any, cardId:string) => Promise<q.CardData> = async (t: any, cardId: string) => {
    try {
        var bd = await getBoardData(t);
        if(bd.cards.containsKey(cardId)) {
            return bd.cards.getValue(cardId);
        }
    }
    catch(e) {
        flashError(t, e);
    }
    return null;
}

export var getDataForCardBack = (t: any, cardId:string) : Promise<q.GetCardBackDataResult> => {
    return new Promise<q.GetCardBackDataResult>(async (resolve, reject) => {
        var isAuthenticated = await config(t).isUserAuthenticated();

        if(!isAuthenticated) {
            resolve(null);
        }
        else {
            if(isCardBackQueryRunning) {
                var sub = cardBackData.subscribe(newValue => {
                    resolve(newValue);
                    sub.dispose();
                });
            }
            else {
                isCardBackQueryRunning = true;
                try {
                    var cardBack : q.GetCardBackDataResult = await channel(t).executeQuery(new q.GetCardBackData(cardId));
                    cardBackData(removeDefaultContextFromCard(cardBack));
                }
                catch(e) {
                    flashError(t, e);
                }
                finally {
                    isCardBackQueryRunning = false;
                }
                resolve(cardBackData());
            }
        }
    });
}


export var isCardLinked: (t: any, cardId:string) => Promise<boolean> = async (t: any, cardId: string) => {
    try {
        var bd = await getBoardData(t);
        return bd.cards.containsKey(cardId);
    }
    catch(e) {
        flashError(t, e);
    }
}

export var getStorymapProperties = async (t: any): Promise<IStorymapProperties> => {
    if(boardData() != null) {
        return boardData();
    }

    try {
        var bd = await getBoardData(t);
        return bd;
    }
    catch(e) {
        flashError(t, e);
    }
}

export var getStorymapUrl: (t: any) => Promise<string> = async (t: any) => {
    try {
        if(boardData() == null) {
            await getBoardData(t);
        }

        return boardData()?.storymapUrl;
    }
    catch(e) {
        flashError(t, e);
    }
}

var removeDefaultContextFromCard = <TCard extends q.CardData>(card: TCard) => {
    if(card.Goal == "Cards from Trello") {
        card.Goal = null;
    }

    if(card.Step.match(/^from (.*) list$/i)) {
        card.Step = null;
    }

    return card;
}


class Attachment{
    public name: string;
    public url: string;
    public thumbnailUrl: string;
    public extension: string;
    public isWebLink: boolean;
    public isImage: boolean;
    public isFile: boolean;
    public addedAt: string;
}

class BoardData implements IStorymapProperties {
    constructor(
        public cards: Collections.Dictionary<string, q.CardData>,
        public storymapUrl: string,
        public isEditor: boolean,
        public canSetEstimation: boolean,
        public colorsAndAnnotations: string[]) {
    }
}

export interface IStorymapProperties {
    storymapUrl: string;
    isEditor: boolean;
    colorsAndAnnotations: string[];
    canSetEstimation: boolean;
}
